import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import {
  gdprApi,
  type Consent,
  type ConsentStatus,
  type ConsentType,
} from '@/api/queries/gdpr';
import {
  Button,
  Icon,
  Input,
  StatCard,
  Table,
  useToast,
  type TableColumn,
} from '@/components/ui';

import { ConsentStatusPill } from './ConsentStatusPill';
import { GdprOperationsModal } from './GdprOperationsModal';
import { ResendConsentModal } from './ResendConsentModal';

const TABS: { value: ConsentStatus | 'all'; label: string; dot: string }[] = [
  { value: 'expiring', label: 'Expiring', dot: '#d97706' },
  { value: 'pending', label: 'Pending', dot: '#1f47ab' },
  { value: 'granted', label: 'Granted', dot: '#0f9d6b' },
  { value: 'withdrawn', label: 'Withdrawn', dot: '#c0392b' },
  { value: 'all', label: 'All', dot: '#64748b' },
];

const CONSENT_TYPE_META: Record<
  ConsentType,
  { label: string; color: string; icon: 'mail' | 'send' | 'sparkles' | 'globe' }
> = {
  newsletter: { label: 'Newsletter', color: '#1F47AB', icon: 'mail' },
  marketing: { label: 'Marketing', color: '#D97706', icon: 'send' },
  research: { label: 'Research', color: '#7A4FB5', icon: 'sparkles' },
  all: { label: 'All channels', color: '#64748B', icon: 'globe' },
};

const AVATAR_COLORS = [
  '#1F47AB', '#0F9D6B', '#7A4FB5', '#D97706', '#0E8E8E', '#C0392B', '#B45309', '#64748B',
];

function avatarColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initials(name?: string | null, email?: string | null): string {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

export function GdprPage() {
  const search = useSearch({ from: '/gdpr/' });
  const navigate = useNavigate({ from: '/gdpr/' });
  const { data: list, isPending } = gdprApi.useListConsents({
    status: search.tab,
    type: search.type,
    q: search.q,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 25,
  });
  const { data: stats } = gdprApi.useStats();
  const withdraw = gdprApi.useWithdraw();
  const runRenewal = gdprApi.useRunRenewalJob();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [resendTarget, setResendTarget] = useState<Consent | null>(null);
  const [bulkResendOpen, setBulkResendOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);

  const setTab = (tab: ConsentStatus | 'all') =>
    navigate({ search: (prev) => ({ ...prev, tab, page: 1 }) });
  const setQuery = (q: string) =>
    navigate({ search: (prev) => ({ ...prev, q: q || undefined, page: 1 }) });
  const setType = (type: typeof search.type) =>
    navigate({ search: (prev) => ({ ...prev, type, page: 1 }) });

  const consents = list?.data ?? [];

  const tabCount = (value: ConsentStatus | 'all'): number | undefined => {
    if (!stats) return undefined;
    switch (value) {
      case 'expiring':
        return stats.expiring30;
      case 'pending':
        return stats.pending;
      case 'granted':
        return stats.active;
      case 'withdrawn':
        return stats.withdrawn30;
      case 'all':
        return stats.active + stats.pending + stats.withdrawn30;
    }
  };

  const bulkWithdraw = async () => {
    if (!confirm(`Withdraw consent for ${selected.size} contact(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    let ok = 0;
    try {
      for (const id of selected) {
        await withdraw.mutateAsync(id);
        ok += 1;
      }
      toast.show({ severity: 'success', title: `Withdrew ${ok} consent${ok === 1 ? '' : 's'}` });
      setSelected(new Set());
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : `Bulk withdraw failed after ${ok}`,
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const columns: TableColumn<Consent>[] = [
    {
      id: 'contact',
      header: 'Contact',
      cell: (c) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: avatarColor(c.contactEmail || c.contactId),
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials(c.contactName, c.contactEmail)}
          </span>
          <div style={{ minWidth: 0 }}>
            <strong>{c.contactName || c.contactEmail || '—'}</strong>
            {c.contactEmail && c.contactName && (
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.contactEmail}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type / Scope',
      width: 220,
      cell: (c) => {
        const meta = CONSENT_TYPE_META[c.type] ?? CONSENT_TYPE_META.all;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: `${meta.color}1a`,
                color: meta.color,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name={meta.icon} size={12} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{meta.label}</div>
              {c.scope && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.scope}</div>}
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: 140,
      cell: (c) => <ConsentStatusPill status={c.status} expiresAt={c.expiresAt ?? undefined} />,
    },
    {
      id: 'source',
      header: 'Source',
      width: 160,
      cell: (c) => c.source || <span style={{ color: 'var(--text-4)' }}>—</span>,
    },
    {
      id: 'granted',
      header: 'Granted',
      width: 130,
      cell: (c) =>
        c.grantedAt ? new Date(c.grantedAt).toLocaleDateString() : '—',
    },
    {
      id: 'expires',
      header: 'Expires',
      width: 130,
      cell: (c) => {
        if (!c.expiresAt) return '—';
        const d = new Date(c.expiresAt);
        const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
        const color =
          days < 0 ? 'var(--red, #c0392b)' : days <= 30 ? 'var(--amber, #d97706)' : undefined;
        return (
          <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
            {d.toLocaleDateString()}
          </span>
        );
      },
    },
    {
      id: 'reminders',
      header: 'Reminders',
      width: 100,
      cell: (c) =>
        c.remindersSent > 0 ? (
          `${c.remindersSent}×`
        ) : (
          <span style={{ color: 'var(--text-4)' }}>—</span>
        ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">GDPR</h1>
          <p className="page-subtitle">
            Manage contact consents under EU 2016/679. Review expiring grants, run renewal
            campaigns, handle DSAR and right-to-be-forgotten requests.
          </p>
        </div>
        <div className="page-actions">
          <Button
            variant="ghost"
            leftIcon={<Icon name="refresh" size={14} />}
            loading={runRenewal.isPending}
            onClick={async () => {
              try {
                const res = await runRenewal.mutateAsync();
                toast.show({
                  severity: 'success',
                  title: `Marked ${res.markedExpiring} consents as expiring`,
                });
              } catch (err) {
                toast.show({
                  severity: 'error',
                  title: err instanceof Error ? err.message : 'Renewal sweep failed',
                });
              }
            }}
          >
            Run renewal sweep
          </Button>
          <Button
            variant="primary"
            leftIcon={<Icon name="settings" size={14} />}
            onClick={() => setOpsOpen(true)}
          >
            GDPR operations
          </Button>
        </div>
      </div>

      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
            marginBottom: 14,
          }}
        >
          <StatCard label="Active consents" value={stats.active} tone="brand" icon="shield" />
          <StatCard label="Expiring (30d)" value={stats.expiring30} tone="amber" icon="clock" />
          <StatCard label="Pending opt-in" value={stats.pending} tone="info" icon="mail" />
          <StatCard label="Withdrawn (30d)" value={stats.withdrawn30} tone="muted" icon="x" />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--border)',
          marginBottom: 12,
        }}
      >
        {TABS.map((t) => {
          const active = (search.tab ?? 'expiring') === t.value;
          const count = tabCount(t.value);
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
                color: active ? 'var(--text-1)' : 'var(--text-3)',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              <span
                style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot }}
              />
              {t.label}
              {count !== undefined && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-3)',
                    background: 'var(--surface-2)',
                    borderRadius: 999,
                    padding: '1px 7px',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selected.size > 0 ? (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            background: 'var(--brand-soft)',
            padding: '8px 12px',
            borderRadius: 6,
            marginBottom: 10,
          }}
        >
          <strong>{selected.size} selected</strong>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Icon name="send" size={12} />}
            disabled={bulkBusy}
            onClick={() => setBulkResendOpen(true)}
          >
            Resend confirmation
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Icon name="x" size={12} />}
            loading={bulkBusy}
            onClick={() => void bulkWithdraw()}
          >
            Withdraw
          </Button>
          <a
            href={gdprApi.buildLogExportURL()}
            className="ds-btn ds-btn-ghost ds-btn-sm"
            style={{ textDecoration: 'none' }}
          >
            <Icon name="download" size={12} /> Export log
          </a>
          <Button
            variant="ghost"
            size="sm"
            disabled={bulkBusy}
            onClick={() => setSelected(new Set())}
          >
            Clear
          </Button>
        </div>
      ) : (
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <Input
            placeholder="Search name, email, scope"
            value={search.q ?? ''}
            onChange={(e) => setQuery(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <select
            className="ds-input"
            style={{ width: 160 }}
            value={search.type ?? 'all'}
            onChange={(e) =>
              setType(
                e.currentTarget.value === 'all'
                  ? undefined
                  : (e.currentTarget.value as typeof search.type),
              )
            }
          >
            <option value="all">All types</option>
            <option value="newsletter">Newsletter</option>
            <option value="marketing">Marketing</option>
            <option value="research">Research</option>
          </select>
          <a
            href={gdprApi.buildLogExportURL()}
            className="ds-btn ds-btn-ghost ds-btn-sm"
            style={{ marginLeft: 'auto', textDecoration: 'none' }}
          >
            <Icon name="download" size={12} /> Consent log CSV
          </a>
        </div>
      )}

      <Table<Consent>
        columns={columns}
        rows={consents}
        rowKey={(c) => c.id}
        loading={isPending}
        emptyMessage="No consents in this view."
        selection={{
          selectedIds: selected,
          onChange: setSelected,
        }}
        rowActions={(c) => (
          <div style={{ display: 'flex', gap: 4 }}>
            {(c.status === 'expiring' || c.status === 'pending') && (
              <Button
                size="sm"
                variant="ghost"
                aria-label="Resend"
                onClick={() => setResendTarget(c)}
              >
                <Icon name="send" size={12} />
              </Button>
            )}
            {c.status !== 'withdrawn' && (
              <Button
                size="sm"
                variant="ghost"
                aria-label="Withdraw"
                onClick={async () => {
                  if (!confirm(`Withdraw consent for ${c.contactEmail || c.id}?`)) return;
                  try {
                    await withdraw.mutateAsync(c.id);
                    toast.show({ severity: 'success', title: 'Consent withdrawn' });
                  } catch (err) {
                    toast.show({
                      severity: 'error',
                      title: err instanceof Error ? err.message : 'Failed to withdraw',
                    });
                  }
                }}
              >
                <Icon name="x" size={12} />
              </Button>
            )}
          </div>
        )}
      />

      {resendTarget && (
        <ResendConsentModal
          mode="one"
          target={resendTarget}
          onClose={() => setResendTarget(null)}
        />
      )}
      {bulkResendOpen && (
        <ResendConsentModal
          mode="bulk"
          consentIds={Array.from(selected)}
          onClose={() => {
            setBulkResendOpen(false);
            setSelected(new Set());
          }}
        />
      )}
      {opsOpen && <GdprOperationsModal onClose={() => setOpsOpen(false)} />}
    </div>
  );
}

